import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { isGoogleCalendarConnected } from '@/services/googleCalendarApi'

/**
 * Check if user should see the Google Calendar connect modal
 * @param userId - The user's Firebase UID
 * @returns Promise<boolean> - True if they should see the modal
 */
export async function shouldShowCalendarModal(userId: string): Promise<boolean> {
  try {
    // Get user type from Firestore first
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) {
      return false
    }
    
    const userData = userDoc.data()
    const userType = userData.userType || 'student'
    
    // Only check for tutors - students don't need Google Calendar
    if (userType !== 'tutor') {
      return false
    }
    
    // Don't show modal if email is not verified yet (still in registration)
    const isEmailVerified = userData.isEmailVerified || false
    if (!isEmailVerified) {
      return false
    }
    
    // For tutors: Check if user has Google Calendar connected
    const isConnected = await isGoogleCalendarConnected(userId)
    
    // Show modal if not connected (tokens don't exist or were cleared due to expiry)
    return !isConnected
    
  } catch (error) {
    console.error('Error checking calendar modal status:', error)
    return false
  }
}

/**
 * Mark that the user has seen the Google Calendar modal
 * @param userId - The user's Firebase UID
 * @param connected - Whether they connected Google Calendar
 */
export async function markCalendarModalComplete(
  userId: string, 
  connected: boolean = false
): Promise<void> {
  try {
    const userDocRef = doc(db, 'users', userId)
    await setDoc(userDocRef, {
      hasSeenCalendarModal: true,
      googleCalendarConnected: connected,
      calendarModalCompletedAt: new Date()
    }, { merge: true })
    
    console.log('Calendar modal marked as complete for user:', userId)
  } catch (error) {
    console.error('Error marking calendar modal complete:', error)
  }
}
